/**
 * A small bar chart, inline SVG, no library.  Meeting-notes foundation (2026-10-31): one chart for the branches that
 * show «how many saw it», gifts in and out, touches per month — so they do not each draw their own.
 *
 *  - Responsive: a viewBox, width 100 %; the panel's green for the series, the muted tone for a secondary one (in/out pairs).
 *  - Every bar has its label under it (twelve months are «jan … des»); the value is in a <title> (hover and focus) and in a
 *    visually hidden table for screen readers; the whole figure carries an aria-label summary.
 *  - All zero → «Ingen tall ennå», nothing drawn. Negative values are drawn as 0 (a bar chart of amounts, not of change).
 *  - The SVG is aria-hidden: screen readers read the hidden table (its caption is the aria summary), not twelve focus stops.
 *  - Numbers through the panel's own formatters: kr() for money (lib/format.ts), nb-NO grouping for counts. Never a new one.
 */
import React from 'react';
import { kr } from '../lib/format';

export interface BarSeriesPoint { label: string; value: number; hint?: string }
export interface BarChartProps {
  series: BarSeriesPoint[];
  unit?: 'kr' | 'count';
  height?: number;
  /** The top of the scale; by default the largest value drawn. */
  max?: number;
  /** A second value per bar (gifts out beside gifts in), drawn in the muted tone; matched by position. */
  secondary?: { label: string; value: number }[];
  ariaLabel: string;
  /** The names of the two series in the screen-reader table (default «Verdi» / «Sekundær»), e.g. «Inn» / «Ut». */
  seriesLabels?: [string, string];
}

/** Short Norwegian month labels, as the owner's panel writes them. */
export const MONTHS_NO = ['jan', 'feb', 'mar', 'apr', 'mai', 'jun', 'jul', 'aug', 'sep', 'okt', 'nov', 'des'];

/** A count as Norwegians write it: 1200 → «1 200». Money goes through kr(). */
export const fmtValue = (v: number, unit: 'kr' | 'count'): string =>
  unit === 'kr' ? kr(v) : new Intl.NumberFormat('nb-NO', { maximumFractionDigits: 0 }).format(Number.isFinite(v) ? v : 0).replace(/\s/g, ' ');

const W = 600;
const PAD = { top: 8, right: 8, bottom: 22, left: 8 };
const SR_ONLY: React.CSSProperties = { position: 'absolute', width: 1, height: 1, padding: 0, margin: -1, overflow: 'hidden', clip: 'rect(0 0 0 0)', whiteSpace: 'nowrap', border: 0 };

export function BarChart({ series, unit = 'count', height = 160, max, secondary, ariaLabel, seriesLabels }: BarChartProps) {
  const safe = (v: number) => (Number.isFinite(v) && v > 0 ? v : 0);
  const values = series.map((s) => safe(s.value));
  const second = secondary ? series.map((_, i) => safe(secondary[i] ? secondary[i].value : 0)) : null;
  const top = Math.max(safe(max ?? 0), ...values, ...(second || [0]));
  const empty = series.length === 0 || top === 0;
  const pairs = Boolean(second);
  const n = Math.max(series.length, 1);
  const slot = (W - PAD.left - PAD.right) / n;
  const gap = Math.min(10, slot * 0.25);
  const barW = pairs ? (slot - gap) / 2 : slot - gap;
  const plotH = height - PAD.top - PAD.bottom;
  const y = (v: number) => PAD.top + plotH - (top > 0 ? (v / top) * plotH : 0);
  const r = Math.min(4, barW / 2);
  // A bar with rounded TOP corners only (a path, so the base sits flat on the axis).
  const bar = (x: number, v: number) => {
    const h = Math.max(0, PAD.top + plotH - y(v));
    if (h <= 0) return '';
    const rr = Math.min(r, h);
    const yTop = y(v), yBot = PAD.top + plotH;
    return `M${x},${yBot} V${yTop + rr} Q${x},${yTop} ${x + rr},${yTop} H${x + barW - rr} Q${x + barW},${yTop} ${x + barW},${yTop + rr} V${yBot} Z`;
  };
  const describe = (s: BarSeriesPoint, i: number) =>
    `${s.label}: ${fmtValue(values[i], unit)}${second ? ` / ${fmtValue(second[i], unit)}` : ''}${s.hint ? ` (${s.hint})` : ''}`;

  if (empty) {
    return <p className="note" role="status"><span style={SR_ONLY}>{ariaLabel}: </span>Ingen tall ennå</p>;
  }
  return (
    <figure className="barchart" style={{ margin: 0 }}>
      <svg viewBox={`0 0 ${W} ${height}`} width="100%" height={height} aria-hidden="true" focusable="false" style={{ display: 'block', overflow: 'visible' }}>
        <line x1={PAD.left} x2={W - PAD.right} y1={PAD.top + plotH} y2={PAD.top + plotH} stroke="var(--line, rgba(0,0,0,.12))" strokeWidth={1} />
        {series.map((s, i) => {
          const x0 = PAD.left + i * slot + gap / 2;
          return (
            <g key={i}>
              <title>{describe(s, i)}</title>
              {values[i] > 0 && <path d={bar(x0, values[i])} fill="var(--green, #006344)" />}
              {second && second[i] > 0 && <path d={bar(x0 + barW, second[i])} fill="var(--text-muted, #56655D)" opacity={0.55} />}
              <text x={x0 + (pairs ? barW : barW / 2)} y={height - 6} textAnchor="middle" fontSize={11} fill="var(--text-muted, #56655D)" fontFamily="var(--font-form, inherit)">{s.label}</text>
            </g>
          );
        })}
      </svg>
      <table style={SR_ONLY}>
        <caption>{ariaLabel}</caption>
        <thead><tr><th scope="col">Periode</th><th scope="col">{seriesLabels ? seriesLabels[0] : 'Verdi'}</th>{second && <th scope="col">{seriesLabels ? seriesLabels[1] : 'Sekundær'}</th>}</tr></thead>
        <tbody>
          {series.map((s, i) => (
            <tr key={i}><th scope="row">{s.label}</th><td>{fmtValue(values[i], unit)}</td>{second && <td>{fmtValue(second[i], unit)}</td>}</tr>
          ))}
        </tbody>
      </table>
    </figure>
  );
}

export default BarChart;
